import React, { useState, useEffect, useRef } from 'react';
import { message, Spin, Modal, Form, Input, InputNumber, Select, Popconfirm, Radio } from 'antd';
import {
  CloudServerOutlined,
  PlusOutlined,
  DeleteOutlined,
  SyncOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { cloudServerApi } from '../services/api';

const { Option } = Select;

interface ServerInfo {
  id: number;
  cloud_srv_id: string;
  srv_name: string;
  srv_status: string;
  gpu_model: string;
  price_per_hour: number;
  comfyui_status: string;
  created_at: string;
  task_running_status: string;
}

interface GpuModelOption {
  value: string;
  label: string;
}

interface DeployConfig {
  gpu_models: GpuModelOption[];
  image_ids: string[];
  image_types: string[];
}

interface DeployForm {
  gpu_model: string;
  gpu_count: number;
  image_id: string;
  image_type: string;
  srv_name: string;
}

const CloudServerPageNexus: React.FC = () => {
  const [servers, setServers] = useState<ServerInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [deployModalVisible, setDeployModalVisible] = useState(false);
  const [deployConfig, setDeployConfig] = useState<DeployConfig>({
    gpu_models: [],
    image_ids: [],
    image_types: []
  });
  const [deployForm] = Form.useForm();
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshClickRef = useRef<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loadError, setLoadError] = useState(false);
  const [autoDestroyModalVisible, setAutoDestroyModalVisible] = useState(false);
  const [autoDestroyMode, setAutoDestroyMode] = useState<'0' | '1'>('0');
  const [autoDestroyDraftMode, setAutoDestroyDraftMode] = useState<'0' | '1'>('0');
  const [autoDestroySaving, setAutoDestroySaving] = useState(false);
  const isAutoDestroyEnabled = autoDestroyMode === '1';

  // 服务器状态映射
  const statusMap: Record<string, { text: string; color: string }> = {
    running: { text: '正在运行', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    booting: { text: '正在开机', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    deploying: { text: '正在部署', color: 'bg-green-50 text-green-700 border-green-200' },
    shutting_down: { text: '正在关机', color: 'bg-orange-50 text-orange-700 border-orange-200' },
    shutdown: { text: '已关机', color: 'bg-red-50 text-red-700 border-red-200' },
    destroying: { text: '正在销毁', color: 'bg-red-50 text-red-700 border-red-200' },
    destroyed: { text: '已销毁', color: 'bg-red-50 text-red-700 border-red-200' },
    saving_image: { text: '正在储存镜像', color: 'bg-purple-50 text-purple-700 border-purple-200' },
    freezing: { text: '正在冻结', color: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
    freeze: { text: '已冻结', color: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
    replacing_image: { text: '更换镜像', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' }
  };

  const taskStatusMap: Record<string, { text: string; color: string }> = {
    'idle': { text: '空闲', color: 'bg-green-50 text-green-700 border-green-200' },
    'executing': { text: '使用中', color: 'bg-orange-50 text-orange-700 border-orange-200' }
  };

  const comfyuiStatusMap: Record<string, { text: string; color: string }> = {
    'connected': { text: '已连接', color: 'bg-green-50 text-green-700 border-green-200' },
    'disconnected': { text: '断开连接', color: 'bg-red-50 text-red-700 border-red-200' }
  };

  const getRefreshInterval = () => {
    return parseInt(import.meta.env.VITE_SERVER_REFRESH_INTERVAL || '30') * 1000;
  };

  const loadServers = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await cloudServerApi.getServerList();
      if (response.success && response.data) {
        setServers(response.data);
        setLoadError(false);
      } else if (!silent) {
        message.error('获取服务器列表失败');
      }
    } catch (error) {
      console.error('获取服务器列表失败:', error);
      if (!silent) {
        setLoadError(true);
        message.error('获取服务器列表失败，请检查网络连接');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadDeployConfig = async (silent = false) => {
    try {
      const response = await cloudServerApi.getDeployConfig();
      if (response.success && response.data) {
        setDeployConfig(response.data);
      } else if (!silent) {
        message.error('获取部署配置失败');
      }
    } catch (error) {
      console.error('获取部署配置失败:', error);
      if (!silent) {
        message.error('获取部署配置失败');
      }
    }
  };

  // Load the current user's automatic server destroy switch.
  const loadAutoDestroyConfig = async (silent = false) => {
    try {
      const response = await cloudServerApi.getAutoDestroyConfig();
      if (response.success && response.data) {
        const configValue = response.data.config_value === '1' ? '1' : '0';
        setAutoDestroyMode(configValue);
        setAutoDestroyDraftMode(configValue);
      } else if (!silent) {
        message.error('获取自动销毁配置失败');
      }
    } catch (error) {
      console.error('获取自动销毁配置失败:', error);
      if (!silent) {
        message.error('获取自动销毁配置失败');
      }
    }
  };

  // Save the current user's automatic server destroy switch.
  const saveAutoDestroyConfig = async () => {
    setAutoDestroySaving(true);
    try {
      const response = await cloudServerApi.updateAutoDestroyConfig(autoDestroyDraftMode);
      if (response.success && response.data) {
        const configValue = response.data.config_value === '1' ? '1' : '0';
        setAutoDestroyMode(configValue);
        setAutoDestroyDraftMode(configValue);
        setAutoDestroyModalVisible(false);
        message.success('自动销毁配置已保存');
      } else {
        message.error(response.message || '保存自动销毁配置失败');
      }
    } catch (error) {
      console.error('保存自动销毁配置失败:', error);
      message.error('保存自动销毁配置失败');
    } finally {
      setAutoDestroySaving(false);
    }
  };

  // Open the auto-destroy switch modal with the last saved value.
  const openAutoDestroyModal = () => {
    setAutoDestroyDraftMode(autoDestroyMode);
    setAutoDestroyModalVisible(true);
  };

  // Close the auto-destroy switch modal without saving draft changes.
  const closeAutoDestroyModal = () => {
    setAutoDestroyDraftMode(autoDestroyMode);
    setAutoDestroyModalVisible(false);
  };

  const refreshServerStatus = async (cloudSrvId: string) => {
    const hide = message.loading('正在刷新服务器状态...', 0);
    try {
      await loadServers();
      hide();
      message.success('服务器状态已刷新');
    } catch (error: any) {
      hide();
      console.error('刷新服务器状态失败:', error);
      const errorMsg = error?.response?.data?.message || error?.message || '未知错误';
      message.error(`刷新服务器状态失败: ${errorMsg}`);
    }
  };

  const destroyServer = async (cloudSrvId: string) => {
    try {
      const response = await cloudServerApi.destroyServer(cloudSrvId);
      if (response.success) {
        message.success('销毁请求已提交到队列，请等待处理');
        loadServers();
      } else {
        message.error(response.message || '销毁服务器失败');
      }
    } catch (error) {
      console.error('销毁服务器失败:', error);
      message.error('销毁服务器失败');
    }
  };

  const deployServer = async (values: DeployForm) => {
    try {
      console.log('部署参数:', values);
      const response = await cloudServerApi.deployServer(values);
      if (response.success) {
        message.success('部署消息已发送，请等待处理');
        setDeployModalVisible(false);
        deployForm.resetFields();
        loadServers();
      } else {
        message.error(response.message || '部署服务器失败');
      }
    } catch (error: any) {
      console.error('部署服务器失败:', error);
      const errorMsg = error?.response?.data?.message || error?.message || '未知错误';
      message.error(`部署服务器失败: ${errorMsg}`);
    }
  };

  const handleCheckWorkflowStatus = async () => {
    setCheckingStatus(true);
    try {
      const response = await cloudServerApi.checkWorkflowStatus();
      if (response.success) {
        message.success('工作流状态检查完成');
        loadServers();
      } else {
        message.error(response.message || '获取工作流状态失败');
      }
    } catch (error: any) {
      console.error('获取工作流状态失败:', error);
      const errorMsg = error?.response?.data?.message || error?.message || '未知错误';
      message.error(`获取工作流状态失败: ${errorMsg}`);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleRestartComfyUI = async (cloudSrvId: string) => {
    try {
      const response = await cloudServerApi.restartComfyUI(cloudSrvId);
      if (response.success) {
        message.success('服务进程重启成功');
        loadServers();
      } else {
        message.error(response.message || 'ComfyUI进程重启失败');
      }
    } catch (error: any) {
      console.error('重启服务进程失败:', error);
      const errorMsg = error?.response?.data?.message || error?.message || '未知错误';
      message.error(`重启服务进程失败: ${errorMsg}`);
    }
  };

  const generateServerName = () => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `CloudServer-${randomStr}-${timestamp.toString().slice(-6)}`;
  };

  const handleOpenDeployModal = () => {
    deployForm.setFieldsValue({
      srv_name: generateServerName(),
      gpu_count: 1,
      image_id: deployConfig?.image_ids?.[0] || undefined,
      image_type: deployConfig?.image_types?.[0] || undefined,
      gpu_model: deployConfig?.gpu_models?.[0]?.value || undefined
    });
    setDeployModalVisible(true);
  };

  const setupAutoRefresh = () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    
    const interval = getRefreshInterval();
    refreshIntervalRef.current = setInterval(() => {
      loadServers(true);
    }, interval);
  };

  const clearAutoRefresh = () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  };

  useEffect(() => {
    loadServers();
    loadDeployConfig();
    loadAutoDestroyConfig(true);
    setupAutoRefresh();

    // 网络恢复时自动重新加载
    const handleOnline = () => {
      console.log('🌐 网络已恢复，重新加载数据');
      loadServers();
      loadDeployConfig(true);
      loadAutoDestroyConfig(true);
    };

    // 页面变为可见时重新加载（用户切换回来时）
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadServers(true);
      }
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearAutoRefresh();
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 分页计算
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedServers = servers.slice(startIndex, endIndex);
  const totalPages = Math.ceil(servers.length / pageSize);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-amber-800">
          <ExclamationCircleOutlined style={{ fontSize: '20px' }} />
          <span className="text-base font-medium">云服务器使用完成后，请及时关闭，避免产生额外计费。</span>
        </div>

        {/* 页面标题和操作按钮 */}
        <div className="mb-8 bg-white rounded-2xl p-6 border border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">云服务器管理</h1>
              <p className="text-lg text-gray-600">管理和监控您的云服务器实例</p>
            </div>
            <div className="flex space-x-3">
          <button
            onClick={() => {
              const now = Date.now();
              if (now - lastRefreshClickRef.current < 3000) {
                message.warning('操作过于频繁，请3秒后再试');
                return;
              }
              lastRefreshClickRef.current = now;
              loadServers();
            }}
            disabled={loading}
            className="px-6 py-3 bg-white text-gray-700 border border-gray-200 rounded-xl font-medium hover:border-indigo-200 transition-all flex items-center space-x-2"
          >
            <ReloadOutlined style={{ fontSize: '18px' }} />
            <span>刷新服务器</span>
          </button>
          <button
            onClick={handleCheckWorkflowStatus}
            disabled={checkingStatus}
            className="px-6 py-3 bg-white text-gray-700 border border-gray-200 rounded-xl font-medium hover:border-indigo-200 transition-all flex items-center space-x-2"
          >
            <SyncOutlined style={{ fontSize: '18px' }} />
            <span>获取工作流状态</span>
          </button>
          <button
            onClick={openAutoDestroyModal}
            className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center space-x-2 ${
              isAutoDestroyEnabled
                ? 'bg-green-50 text-green-700 border border-green-200 hover:border-green-300 hover:bg-green-100'
                : 'bg-red-50 text-red-700 border border-red-200 hover:border-red-300 hover:bg-red-100'
            }`}
          >
            {isAutoDestroyEnabled ? (
              <CheckCircleOutlined style={{ fontSize: '18px' }} />
            ) : (
              <DeleteOutlined style={{ fontSize: '18px' }} />
            )}
            <span>{isAutoDestroyEnabled ? '自动销毁' : '手动销毁'}</span>
          </button>
          <button
            onClick={handleOpenDeployModal}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all flex items-center space-x-2"
          >
            <PlusOutlined style={{ fontSize: '18px' }} />
            <span>创建服务器</span>
          </button>
            </div>
          </div>
        </div>

        {/* 服务器列表 */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Spin size="large" />
          </div>
        ) : loadError && servers.length === 0 ? (
          <div className="flex flex-col justify-center items-center py-20 bg-white rounded-2xl border border-gray-200">
            <CloudServerOutlined style={{ fontSize: '64px', color: '#EF4444' }} />
            <p className="mt-4 text-gray-700 text-lg font-medium">数据加载失败</p>
            <p className="mt-2 text-gray-500">网络连接不稳定，请检查网络后重试</p>
            <button
              onClick={() => { loadServers(); loadDeployConfig(); }}
              className="mt-6 px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all flex items-center space-x-2"
            >
              <ReloadOutlined style={{ fontSize: '16px' }} />
              <span>重新加载</span>
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700" style={{ width: '110px' }}>服务器名称</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700" style={{ width: '110px' }}>服务器ID</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700" style={{ width: '110px' }}>运行状态</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700" style={{ width: '110px' }}>占用情况</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700" style={{ width: '100px' }}>GPU模型</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700" style={{ width: '100px' }}>小时计费</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700" style={{ width: '120px' }}>工作流状态</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700" style={{ width: '120px' }}>创建时间</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700" style={{ width: '120px' }}>操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {servers.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-20 text-center">
                        <CloudServerOutlined style={{ fontSize: '64px', color: '#9CA3AF' }} />
                        <p className="mt-4 text-gray-500 text-lg">暂无数据</p>
                      </td>
                    </tr>
                  ) : (
                    paginatedServers.map((server) => {
                    const statusInfo = statusMap[server.srv_status] || { text: server.srv_status, color: 'bg-gray-50 text-gray-700 border-gray-200' };
                    const taskStatus = taskStatusMap[server.task_running_status] || { text: server.task_running_status || '未知', color: 'bg-gray-50 text-gray-700 border-gray-200' };
                    const comfyuiStatus = comfyuiStatusMap[server.comfyui_status] || { text: server.comfyui_status || '-', color: 'bg-gray-50 text-gray-700 border-gray-200' };

                    return (
                      <tr key={server.cloud_srv_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-900">{server.srv_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 font-mono">{server.cloud_srv_id}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-lg text-xs font-medium border ${statusInfo.color}`}>
                            {statusInfo.text}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-lg text-xs font-medium border ${taskStatus.color}`}>
                            {taskStatus.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{server.gpu_model}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {server.price_per_hour ? `¥${server.price_per_hour}/h` : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-lg text-xs font-medium border ${comfyuiStatus.color}`}>
                            {comfyuiStatus.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(server.created_at).toLocaleString('zh-CN')}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => refreshServerStatus(server.cloud_srv_id)}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                              title="刷新实例"
                            >
                              <SyncOutlined style={{ fontSize: '16px' }} />
                            </button>
                            <Popconfirm
                              title="重启服务进程"
                              description={
                                <div>
                                  <p>您即将重启服务器的进程</p>
                                  <p style={{ color: 'orange', marginTop: 8 }}>
                                    ⚠️ 该服务器上正在运行的任务将被中断！
                                  </p>
                                </div>
                              }
                              onConfirm={() => handleRestartComfyUI(server.cloud_srv_id)}
                              okText="确定重启"
                              cancelText="取消"
                              okType="primary"
                            >
                              <button
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                                title="重启服务进程"
                              >
                                <ReloadOutlined style={{ fontSize: '16px' }} />
                              </button>
                            </Popconfirm>
                            <Popconfirm
                              title="⚠️ 销毁云服务器实例"
                              description={
                                <div>
                                  <p>您即将销毁该服务器</p>
                                  <p style={{ color: 'red', marginTop: 8 }}>
                                    ⚠️ 此操作不可逆，服务器将被永久删除！
                                  </p>
                                </div>
                              }
                              onConfirm={() => destroyServer(server.cloud_srv_id)}
                              okText="确定销毁"
                              cancelText="取消"
                              okType="danger"
                            >
                              <button
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                title="销毁实例"
                              >
                                <DeleteOutlined style={{ fontSize: '16px' }} />
                              </button>
                            </Popconfirm>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                  )}
                </tbody>
              </table>
            </div>

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="mt-6 flex justify-between items-center bg-white rounded-2xl p-4 border border-gray-200">
                <div className="text-sm text-gray-600">
                  第 {startIndex + 1}-{Math.min(endIndex, servers.length)} 条，共 {servers.length} 条
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-xl hover:border-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    上一页
                  </button>
                  <span className="px-4 py-2 text-gray-700">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-xl hover:border-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    下一页
                  </button>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                  >
                    <option value={10}>10条/页</option>
                    <option value={20}>20条/页</option>
                    <option value={50}>50条/页</option>
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 部署服务器弹窗 */}
      <Modal
        title="云服务器部署配置"
        open={deployModalVisible}
        onCancel={() => {
          setDeployModalVisible(false);
          deployForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={deployForm}
          layout="vertical"
          onFinish={deployServer}
          initialValues={{
            gpu_count: 1
          }}
        >
          <Form.Item
            label="GPU型号"
            name="gpu_model"
            rules={[{ required: true, message: '请选择GPU型号' }]}
          >
            <Select placeholder="请选择GPU型号" size="large">
              {deployConfig?.gpu_models?.map(model => (
                <Option key={model.value} value={model.value}>{model.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="GPU数量"
            name="gpu_count"
            rules={[{ required: true, message: '请输入GPU数量' }]}
          >
            <InputNumber min={1} max={8} style={{ width: '100%' }} size="large" />
          </Form.Item>

          <Form.Item
            label="镜像ID"
            name="image_id"
            rules={[{ required: true, message: '请选择镜像ID' }]}
          >
            <Select placeholder="请选择镜像ID" size="large">
              {deployConfig?.image_ids?.map(imageId => (
                <Option key={imageId} value={imageId}>{imageId}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="镜像类型"
            name="image_type"
            rules={[{ required: true, message: '请选择镜像类型' }]}
          >
            <Select placeholder="请选择镜像类型" size="large">
              {deployConfig?.image_types?.map(imageType => (
                <Option key={imageType} value={imageType}>{imageType}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="服务器名称"
            name="srv_name"
            rules={[{ required: true, message: '请输入服务器名称' }]}
          >
            <Input 
              placeholder="请输入服务器名称" 
              size="large"
              addonAfter={
                <button
                  type="button"
                  onClick={() => deployForm.setFieldsValue({ srv_name: generateServerName() })}
                  className="text-indigo-600 hover:text-indigo-800 cursor-pointer"
                  style={{ background: 'none', border: 'none', padding: '0 8px' }}
                >
                  重新生成
                </button>
              }
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setDeployModalVisible(false);
                  deployForm.resetFields();
                }}
                className="px-6 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl hover:border-gray-300 transition-all"
              >
                取消
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all"
              >
                部署
              </button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="任务完成后自动销毁"
        open={autoDestroyModalVisible}
        onCancel={closeAutoDestroyModal}
        onOk={saveAutoDestroyConfig}
        confirmLoading={autoDestroySaving}
        okText="确认"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16, color: '#4B5563', lineHeight: 1.7 }}>
          是否在当前用户的任务全部结束后，自动销毁所有运行中的云服务器？
        </div>
        <Radio.Group
          value={autoDestroyDraftMode}
          onChange={(event) => setAutoDestroyDraftMode(event.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <Radio value="0">手动销毁</Radio>
          <Radio value="1">自动销毁</Radio>
        </Radio.Group>
      </Modal>
    </div>
  );
};

export default CloudServerPageNexus;
